import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';

export interface AddToCalendarOptions {
  title: string;
  startDate: Date;
  endDate?: Date;
}

function buildIcsFile(title: string, start: Date, end: Date): File {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (dt: Date) =>
    `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `SUMMARY:${title}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return new File([ics], `${title}.ics`, { type: 'text/calendar' });
}

export async function addToCalendar({ title, startDate, endDate }: AddToCalendarOptions): Promise<void> {
  const end = endDate ?? new Date(startDate.getTime() + 60 * 60 * 1000);

  if (Capacitor.isNativePlatform()) {
    // Approach 1 — native calendar dialog (EventKit / CalendarProvider)
    try {
      const { result } = await CapacitorCalendar.requestWriteOnlyCalendarAccess();
      if (result === 'granted') {
        await CapacitorCalendar.createEventWithPrompt({
          title,
          startDate: startDate.getTime(),
          endDate: end.getTime(),
        });
        return;
      }
    } catch (err) {
      console.warn('CapacitorCalendar unavailable:', err instanceof Error ? err.message : String(err));
    }

    // Approach 2 — OS share sheet (.ics), Calendar app surfaced
    const file = buildIcsFile(title, startDate, end);
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title });
    }
    return;
  }

  const file = buildIcsFile(title, startDate, end);

  // Approach 3 — web share API with .ics file
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return;
    } catch (err) {
      // AbortError means the user dismissed the share sheet — stop here.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // NotAllowedError or any other failure — fall through to download.
    }
  }

  // Approach 4 — anchor download fallback (desktop browsers)
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = `${title}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
